import { GQLQueryOrderInput } from "@rx-cs-system/schema";
import { useCallback } from "react";
import styled from "styled-components";

import { PageBody } from "../PageBody";
import {
  DataListBody,
  DataListBodyItem,
  DataListHeader,
  DataListHeaderItem,
  DataListRow,
  DataTable,
  SwitchHeaderOrder,
} from "../../atoms/Table";
import { Typography } from "../../atoms/Typography";
import { LoadMoreButton } from "../../atoms/LoadMoreButton";
import { DeliveryOrder } from "../../../types";

/**
 * 物品情報ページ
 */

type ColumnName =
  | "name"
  | "productName"
  | "serialNumber"
  | "quantity"
  | "deliveryDate";

export type DeliveryOrderProps = {
  deliveryOrders: DeliveryOrder[];
  order?: GQLQueryOrderInput;
  onSortClick: (input: GQLQueryOrderInput) => void;
  onRowClick: (id: string) => void;
  onLoadMoreClick: (offset: number) => void;
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 30px 40px;
  height: 100%;
`;

const CustomerNameHeaderWrapper = styled.div`
  margin-left: 20px;
`;

const ItemNameHeaderWrapper = styled.div`
  margin-left: 40px;
`;

const DeliveryDateHeaderWrapper = styled.div`
  margin-left: 30px;
`;

const DeliveryDateWrapper = styled.div`
  position: relative;
`;

export function DeliveryOrderPage({
  deliveryOrders,
  order,
  onSortClick,
  onRowClick,
  onLoadMoreClick,
}: DeliveryOrderProps) {
  const sortClick = useCallback(
    (value: ColumnName) => {
      onSortClick(SwitchHeaderOrder(value, order));
    },
    [onSortClick, order],
  );

  const header = (
    <>
      <DataListHeaderItem
        onSort={() => sortClick("name")}
        order={order?.field === "name" ? order.value : undefined}
      >
        <CustomerNameHeaderWrapper>
          <Typography size={1} bold>
            氏名
          </Typography>
        </CustomerNameHeaderWrapper>
      </DataListHeaderItem>
      <DataListHeaderItem>
        <ItemNameHeaderWrapper>
          <Typography size={1} bold>
            物品名
          </Typography>
        </ItemNameHeaderWrapper>
      </DataListHeaderItem>
      <DataListHeaderItem>
        <Typography size={1} bold>
          個数
        </Typography>
      </DataListHeaderItem>
      <DataListHeaderItem>
        <Typography size={1} bold>
          シリアルNo
        </Typography>
      </DataListHeaderItem>
      <DataListHeaderItem
        onSort={() => sortClick("deliveryDate")}
        order={order?.field === "deliveryDate" ? order.value : undefined}
      >
        <DeliveryDateHeaderWrapper>
          <Typography size={1} bold>
            配送日
          </Typography>
        </DeliveryDateHeaderWrapper>
      </DataListHeaderItem>
    </>
  );

  return (
    <PageBody title="配送依頼一覧">
      <Container>
        <DataTable>
          <DataListHeader row={header} flexRatio={[3, 4, 2, 2, 2]} />
          <DataListBody
            rows={deliveryOrders.map((deliveryOrder: DeliveryOrder) => (
              <DataListRow
                flexRatio={[3, 4, 2, 2, 2]}
                key={deliveryOrder.id}
                onRowClick={() => onRowClick(deliveryOrder.id)}
              >
                <DataListBodyItem>
                  <Typography size={2} withEllipsis={true}>
                    {deliveryOrder.name}
                  </Typography>
                </DataListBodyItem>

                <DataListBodyItem>
                  <Typography size={2} withEllipsis={true}>
                    {(deliveryOrder.deliveryHistory &&
                      deliveryOrder.deliveryHistory[0]?.product?.name) ||
                      deliveryOrder.deliveryProducts[0].product.name}
                  </Typography>
                </DataListBodyItem>
                <DataListBodyItem>
                  <Typography size={2} withEllipsis={true}>
                    {(deliveryOrder.deliveryHistory &&
                      deliveryOrder.deliveryProducts[0]?.quantity) ||
                      "-"}
                  </Typography>
                </DataListBodyItem>
                <DataListBodyItem>
                  <Typography size={2} withEllipsis={true}>
                    {(deliveryOrder.deliveryHistory &&
                      deliveryOrder.deliveryHistory[0]?.serialNumber) ||
                      "-"}
                  </Typography>
                </DataListBodyItem>
                <DataListBodyItem>
                  <DeliveryDateWrapper>
                    <Typography size={2} withEllipsis={true}>
                      {deliveryOrder.deliveryDate || "-"}
                    </Typography>
                  </DeliveryDateWrapper>
                </DataListBodyItem>
              </DataListRow>
            ))}
          />
        </DataTable>
        <LoadMoreButton
          onClick={() => onLoadMoreClick(deliveryOrders.length)}
        />
      </Container>
    </PageBody>
  );
}
